import { useDndContext, useDroppable } from '@dnd-kit/core';
import {
  PencilSquareIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import Button from 'components/inputs/button';
import { useFooterConfig } from 'contexts/footer-context';
import { ActiveTabType, SidebarSectionType } from 'data/types';
import { useSaveStructureStepCompletedMutation } from 'features/api/project-api';
import { addManuallyCompletedStep, setActiveTab } from 'features/ui';
import { useStepTipSequence } from 'hooks/use-step-tip-sequence';
import { ReactNode, useCallback, useMemo } from 'react';
import { SidePanelTab } from 'utils/side-panel-tabs';
import { STEP_SIDEBAR_DROP_ZONE_ID } from 'utils/step-sidebar-drop-zone';
import {
  getAccordionNextButtonText,
  getDisabledReason,
  getNextButtonText,
  getStepNumber,
  getStepSidebarPanelColumnClassName,
  isFooterAtLastSection,
  StepActionButtonConfig
} from 'utils/step-sidebar-helpers';
import { confirmationModal } from 'utils/ui';
import SidebarProgressBar from './sidebar-progress-bar';
import StepAccordion from './step-accordion';
import StepProgressBar from './step-progress-bar';

type StepSidebarProps = {
  readonly sections: SidebarSectionType[];
  readonly currentSectionId: string;
  readonly setCurrentSectionId: (sectionId: string) => void;
  readonly onSectionClick?: () => void;
  readonly droppableId?: string;
  readonly footerContent?: ReactNode;
  readonly ringContent?: ReactNode;
  readonly headerRightContent?: ReactNode;
  readonly rightContent?: ReactNode;
  readonly mainRightPanel?: ReactNode;
  readonly centeredHeader?: boolean;
  readonly hideHeaderDescription?: boolean;
  readonly hideProgressBar?: boolean;
  readonly progressBarMaxWidth?: number;
  readonly panelWidth?: number;
  readonly previousTab?: ActiveTabType;
  readonly nextTab?: ActiveTabType;
  readonly onNext?: () => boolean | void;
  readonly subStepCount?: number;
  readonly subStepIndex?: number;
  readonly subStepCanAdvance?: boolean;
  readonly onSubStepChange?: (index: number) => void;
  readonly nextSectionDisabled?: boolean;
  readonly nextSectionDisabledReason?: string;
  readonly nextSectionAlwaysEnabled?: boolean;
  readonly nextButtonText?: string;
  readonly nextVariant?: 'blue' | 'green';
  readonly nextButtonDisabled?: boolean;
  readonly nextButtonDisabledReason?: string;
  readonly confirmNextWhenIncomplete?: {
    title?: string;
    message: string;
    confirmButtonText?: string;
  };
  readonly alternateNextButton?: {
    text: string;
    onClick: () => void;
    disabled?: boolean;
    disabledReason?: string;
  };
  readonly secondaryButton?: {
    text: string;
    onClick: () => void;
    tooltip?: string;
  };
  // Passive inline decoration for the footer next button, shown only on the
  // LAST section (the "Next Section" hop to the next tab) — AHA-6681
  // sub-build 5 readiness indicator. Never affects enablement.
  readonly nextSectionAdornment?: ReactNode;
  readonly headerContent?: ReactNode;
  readonly tabs?: SidePanelTab[];
  readonly activePanelTab?: string;
  readonly onPanelTabChange?: (tabId: string) => void;
  readonly panelVariant?: 'tabs' | 'accordion';
  readonly belowPanelContent?: ReactNode;
  readonly configurationUniqueId?: string;
  readonly viewOnlyMode?: {
    title: string;
    editButtonText: string;
    onEdit: () => void;
  };
};

export { STEP_SIDEBAR_DROP_ZONE_ID };

export default function StepSidebar({
  sections,
  currentSectionId,
  setCurrentSectionId,
  onSectionClick,
  droppableId = STEP_SIDEBAR_DROP_ZONE_ID,
  footerContent,
  ringContent,
  headerRightContent,
  rightContent,
  mainRightPanel,
  centeredHeader = false,
  hideHeaderDescription = false,
  hideProgressBar = false,
  progressBarMaxWidth,
  panelWidth = 340,
  previousTab = 'project',
  nextTab = 'configurations',
  onNext,
  subStepCount,
  subStepIndex,
  subStepCanAdvance,
  onSubStepChange,
  nextSectionDisabled = false,
  nextSectionDisabledReason,
  nextSectionAlwaysEnabled = false,
  nextButtonText,
  nextVariant,
  nextButtonDisabled = false,
  nextButtonDisabledReason,
  confirmNextWhenIncomplete,
  alternateNextButton,
  secondaryButton,
  nextSectionAdornment,
  headerContent,
  tabs,
  activePanelTab,
  onPanelTabChange,
  panelVariant = 'tabs',
  belowPanelContent,
  configurationUniqueId,
  viewOnlyMode
}: StepSidebarProps) {
  const dispatch = useAppDispatch();
  const { structureUniqueId } = useAppSelector((state) => state.structure);
  const manuallyCompletedSteps = useAppSelector(
    (state) => state.ui.manuallyCompletedSteps
  );
  const [saveStepCompleted] = useSaveStructureStepCompletedMutation();
  const { setNodeRef, isOver } = useDroppable({ id: droppableId });
  const { active: activeDrag } = useDndContext();
  const activeDragType = activeDrag?.data?.current?.type as string | undefined;
  const showDropToDelete =
    isOver && !!activeDragType && activeDragType.startsWith('grid-');
  // Placed equipment/panels dragged back here are UN-PLACED, not deleted
  // (AHA-6681 batch 2) — entry/deletion lives on the Existing Equipment page.
  const dropToDeleteLabel =
    activeDragType === 'grid-electrical-panel' ||
    activeDragType === 'grid-existing-equipment'
      ? 'Drop to remove from floor plan'
      : 'Drop to delete';

  const initialSections = useMemo(
    () => sections.filter((s) => s.requirementsMet !== undefined),
    [sections]
  );
  const currentSectionConfig = useMemo(
    () => sections.find((s) => s.id === currentSectionId),
    [sections, currentSectionId]
  );
  const isFullWidthSection = currentSectionConfig?.fullWidthContent ?? false;
  const isCenteredCardSection =
    currentSectionConfig?.centeredCardContent ?? false;
  const isPlainContent = currentSectionConfig?.plainContent ?? false;
  const isHideSidebarPanel = currentSectionConfig?.hideSidebarPanel ?? false;

  const steps = currentSectionConfig?.steps ?? [];

  const currentIndex = sections.findIndex((s) => s.id === currentSectionId);
  const isLastSection = isFooterAtLastSection(
    Boolean(viewOnlyMode),
    currentIndex,
    sections.length
  );
  const currentStepEnableDoneButton = steps[0]?.enableDoneButton ?? false;
  const currentStepDoneButtonDisabled = steps[0]?.doneButtonDisabled ?? false;
  const currentStepComplete = steps[0]?.status === 'complete';
  const currentSectionRequirementsMet =
    currentSectionConfig?.requirementsMet === true;
  const nextButtonAlwaysEnabled =
    currentSectionConfig?.nextButtonAlwaysEnabled ?? false;
  const canMarkComplete =
    (currentStepEnableDoneButton && !currentStepDoneButtonDisabled) ||
    currentStepComplete;
  const allRequiredPreviousSectionsComplete = initialSections
    .filter((s) => s.required !== false && s.id !== currentSectionId)
    .every((s) => s.requirementsMet === true);
  const lastSectionStructuralGuardsPass =
    !nextSectionDisabled && allRequiredPreviousSectionsComplete;
  const structuralGuardsPass = isLastSection
    ? lastSectionStructuralGuardsPass
    : true;
  const hasSubSteps = (subStepCount ?? 0) > 1;
  const onLastSubStep =
    !hasSubSteps || (subStepIndex ?? 0) >= (subStepCount ?? 1) - 1;
  const onFirstSubStep = !hasSubSteps || (subStepIndex ?? 0) <= 0;
  const canCycleToNextSubStep =
    hasSubSteps && !onLastSubStep && !!subStepCanAdvance;
  const requirementsCheckPass =
    nextButtonAlwaysEnabled ||
    confirmNextWhenIncomplete !== undefined ||
    currentSectionRequirementsMet ||
    canCycleToNextSubStep ||
    (isLastSection && nextSectionAlwaysEnabled);
  const canGoNext =
    !nextButtonDisabled && structuralGuardsPass && requirementsCheckPass;

  const { currentStepTipSequence, reopenTipSequence } = useStepTipSequence({
    sections,
    currentSectionId,
    currentStepComplete
  });

  const handleSectionClick = useCallback(
    (sectionId: string) => {
      onSectionClick?.();
      setCurrentSectionId(sectionId);
    },
    [onSectionClick, setCurrentSectionId]
  );

  const handleProgressBarClick = (sectionId: string) => {
    const targetIndex = initialSections.findIndex((s) => s.id === sectionId);
    if (targetIndex < 0) return;

    const previousSections = initialSections.slice(0, targetIndex);
    const allPreviousComplete = previousSections.every(
      (s) => s.requirementsMet === true
    );

    if (allPreviousComplete) {
      handleSectionClick(sectionId);
    }
  };

  const proceedNext = useCallback(() => {
    const stepId = steps[0]?.id ?? currentSectionId;
    dispatch(addManuallyCompletedStep(stepId));
    if (stepId !== currentSectionId) {
      dispatch(addManuallyCompletedStep(currentSectionId));
    }
    if (structureUniqueId && currentSectionConfig?.step_code) {
      const stepCode = currentSectionConfig.step_code;
      saveStepCompleted({
        structureUniqueId,
        stepCode,
        subStepCode: stepId,
        completed: true,
        configurationUniqueId
      })
        .unwrap()
        .catch((err) => console.error('Failed to save step completed:', err));

      if (stepId !== currentSectionId) {
        saveStepCompleted({
          structureUniqueId,
          stepCode,
          subStepCode: currentSectionId,
          completed: true,
          configurationUniqueId
        })
          .unwrap()
          .catch((err) =>
            console.error('Failed to save section completed:', err)
          );
      }

      const allSiblingsComplete = sections
        .filter((s) => s.step_code === stepCode && s.id !== stepId)
        .every((s) => manuallyCompletedSteps.includes(s.id));

      if (allSiblingsComplete) {
        dispatch(addManuallyCompletedStep(stepCode));
        saveStepCompleted({
          structureUniqueId,
          stepCode,
          subStepCode: null,
          completed: true,
          configurationUniqueId
        })
          .unwrap()
          .catch((err) =>
            console.error('Failed to save section completed:', err)
          );
      }
    }

    const skipDefaultNavigation = onNext?.() === false;
    if (skipDefaultNavigation) {
      return;
    }

    if (!viewOnlyMode && currentIndex < sections.length - 1) {
      setCurrentSectionId(sections[currentIndex + 1].id);
    } else {
      dispatch(setActiveTab(nextTab));
    }
  }, [
    currentIndex,
    currentSectionId,
    sections,
    setCurrentSectionId,
    dispatch,
    nextTab,
    onNext,
    viewOnlyMode,
    steps,
    structureUniqueId,
    currentSectionConfig,
    saveStepCompleted,
    manuallyCompletedSteps,
    configurationUniqueId
  ]);

  const handleNext = useCallback(() => {
    if (!viewOnlyMode && hasSubSteps && !onLastSubStep) {
      onSubStepChange?.((subStepIndex ?? 0) + 1);
      return;
    }
    if (confirmNextWhenIncomplete && !currentSectionRequirementsMet) {
      confirmationModal(
        confirmNextWhenIncomplete.message,
        proceedNext,
        confirmNextWhenIncomplete.title,
        undefined,
        confirmNextWhenIncomplete.confirmButtonText
      );
      return;
    }
    proceedNext();
  }, [
    viewOnlyMode,
    hasSubSteps,
    onLastSubStep,
    onSubStepChange,
    subStepIndex,
    confirmNextWhenIncomplete,
    currentSectionRequirementsMet,
    proceedNext
  ]);

  const handlePrevious = useCallback(() => {
    if (viewOnlyMode) {
      dispatch(setActiveTab(previousTab));
      return;
    }
    if (hasSubSteps && !onFirstSubStep) {
      onSubStepChange?.((subStepIndex ?? 0) - 1);
      return;
    }
    if (currentIndex > 0) {
      setCurrentSectionId(sections[currentIndex - 1].id);
    } else {
      dispatch(setActiveTab(previousTab));
    }
  }, [
    viewOnlyMode,
    hasSubSteps,
    onFirstSubStep,
    onSubStepChange,
    subStepIndex,
    currentIndex,
    sections,
    setCurrentSectionId,
    dispatch,
    previousTab
  ]);

  const handleAlternateNext = useCallback(() => {
    const stepId = steps[0]?.id ?? currentSectionId;
    dispatch(addManuallyCompletedStep(stepId));
    if (stepId !== currentSectionId) {
      dispatch(addManuallyCompletedStep(currentSectionId));
    }
    if (structureUniqueId && currentSectionConfig?.step_code) {
      const stepCode = currentSectionConfig.step_code;
      saveStepCompleted({
        structureUniqueId,
        stepCode,
        subStepCode: stepId,
        completed: true,
        configurationUniqueId
      })
        .unwrap()
        .catch((err) => console.error('Failed to save step completed:', err));

      if (stepId !== currentSectionId) {
        saveStepCompleted({
          structureUniqueId,
          stepCode,
          subStepCode: currentSectionId,
          completed: true,
          configurationUniqueId
        })
          .unwrap()
          .catch((err) =>
            console.error('Failed to save section completed:', err)
          );
      }

      const allSiblingsComplete = sections
        .filter((s) => s.step_code === stepCode && s.id !== stepId)
        .every((s) => manuallyCompletedSteps.includes(s.id));

      if (allSiblingsComplete) {
        dispatch(addManuallyCompletedStep(stepCode));
        saveStepCompleted({
          structureUniqueId,
          stepCode,
          subStepCode: null,
          completed: true,
          configurationUniqueId
        })
          .unwrap()
          .catch((err) =>
            console.error('Failed to save section completed:', err)
          );
      }
    }

    alternateNextButton?.onClick();
  }, [
    steps,
    currentSectionId,
    dispatch,
    structureUniqueId,
    currentSectionConfig,
    saveStepCompleted,
    alternateNextButton,
    sections,
    manuallyCompletedSteps,
    configurationUniqueId
  ]);

  const nextDisabledReason = getDisabledReason({
    canGoNext,
    nextButtonDisabledReason,
    currentSectionRequirementsMet,
    canMarkComplete,
    isLastSection,
    nextSectionDisabled,
    nextSectionDisabledReason,
    allRequiredPreviousSectionsComplete,
    currentSectionConfig,
    initialSections,
    currentSectionId
  });

  useFooterConfig(
    useMemo(
      () => ({
        onBack: handlePrevious,
        onNext: handleNext,
        nextText: getNextButtonText({ nextButtonText, isLastSection }),
        nextDisabled: !canGoNext,
        nextDisabledReason,
        nextVariant:
          nextVariant ??
          (isLastSection ? ('green' as const) : ('blue' as const)),
        nextAdornment: isLastSection ? nextSectionAdornment : undefined,
        alternateNextButton: alternateNextButton
          ? {
              text: alternateNextButton.text,
              onClick: handleAlternateNext,
              disabled: alternateNextButton.disabled,
              disabledReason: alternateNextButton.disabledReason
            }
          : undefined,
        secondaryButton
      }),
      [
        handlePrevious,
        handleNext,
        canGoNext,
        isLastSection,
        nextButtonText,
        nextVariant,
        nextDisabledReason,
        nextSectionAdornment,
        alternateNextButton,
        handleAlternateNext,
        secondaryButton
      ]
    )
  );

  const renderAccordionPanel = (accordionTabs: SidePanelTab[]) => {
    const activeTabIndex = accordionTabs.findIndex(
      (t) => t.id === activePanelTab
    );
    const accordionNextButton: StepActionButtonConfig = {
      text: getAccordionNextButtonText({
        nextButtonText,
        nextStepLabel:
          activeTabIndex >= 0
            ? accordionTabs[activeTabIndex + 1]?.label
            : undefined,
        isLastSection
      }),
      onClick: handleNext,
      disabled: !canGoNext,
      disabledReason: nextDisabledReason
    };
    const accordionAlternateButton: StepActionButtonConfig | undefined =
      alternateNextButton
        ? {
            text: alternateNextButton.text,
            onClick: handleAlternateNext,
            disabled: alternateNextButton.disabled,
            disabledReason: alternateNextButton.disabledReason
          }
        : undefined;

    if (viewOnlyMode) {
      return (
        <div className='flex min-h-0 w-full flex-1 flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm'>
          <div className='flex min-h-0 shrink flex-col'>
            <StepAccordion
              tabs={accordionTabs}
              activeTabId={activePanelTab}
              onTabSelect={onPanelTabChange}
              readOnly
            />
          </div>
          <div className='shrink-0'>
            <Button
              text={viewOnlyMode.editButtonText}
              icon={PencilSquareIcon}
              fullWidth
              onClick={viewOnlyMode.onEdit}
            />
          </div>
        </div>
      );
    }

    return (
      <div className='flex min-h-0 w-full flex-1 flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm'>
        <SidebarProgressBar
          stepNumber={activeTabIndex >= 0 ? activeTabIndex + 1 : 1}
          totalSteps={accordionTabs.length}
        />
        <div
          ref={setNodeRef}
          className={clsx(
            'relative flex min-h-0 flex-1 flex-col gap-3 rounded-lg',
            isOver && 'bg-red-50 ring-2 ring-red-400'
          )}
        >
          <StepAccordion
            tabs={accordionTabs}
            activeTabId={activePanelTab}
            onTabSelect={onPanelTabChange}
            nextButton={accordionNextButton}
            alternateNextButton={accordionAlternateButton}
          />
          {isOver && (
            <div className='shrink-0 rounded-lg border border-red-200 bg-red-100 p-2 text-center text-xs font-semibold text-red-700'>
              {dropToDeleteLabel}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPanelContent = () => {
    if (panelVariant === 'accordion' && tabs && tabs.length > 0) {
      return renderAccordionPanel(tabs);
    }

    const activeTab = tabs?.find((t) => t.id === activePanelTab);
    const activeTabRenderContent = activeTab?.renderContent;
    const activeTabLoading = activeTab?.isLoading ?? false;
    const panelContent = tabs
      ? activeTabRenderContent?.()
      : currentSectionConfig?.renderPanelContent?.();

    return (
      <div
        ref={setNodeRef}
        id={droppableId}
        className={clsx(
          'relative flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white',
          showDropToDelete && 'border-red-500 bg-red-50'
        )}
      >
        {tabs && tabs.length > 0 && (
          <div className='flex shrink-0 flex-wrap border-b border-gray-200'>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type='button'
                disabled={tab.disabled}
                onClick={() => {
                  if (!tab.disabled) {
                    onPanelTabChange?.(tab.id);
                  }
                }}
                className={clsx(
                  'whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors',
                  tab.disabled && 'cursor-not-allowed text-gray-300',
                  !tab.disabled &&
                    activePanelTab === tab.id &&
                    'border-b-2 border-blue-500 text-blue-600',
                  !tab.disabled &&
                    activePanelTab !== tab.id &&
                    'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                )}
              >
                <span className='flex items-center gap-1.5'>
                  {tab.label}
                  {tab.isLoading && (
                    <svg
                      className='h-3.5 w-3.5 animate-spin'
                      xmlns='http://www.w3.org/2000/svg'
                      viewBox='0 0 512 512'
                    >
                      <path
                        fill='currentColor'
                        className='opacity-30'
                        d='M256 64C150 64 64 150 64 256s86 192 192 192c70.1 0 131.3-37.5 164.9-93.6l.1 .1c-6.9 14.9-1.5 32.8 13 41.2c15.3 8.9 34.9 3.6 43.7-11.7c.2-.3 .4-.6 .5-.9l0 0C434.1 460.1 351.1 512 256 512C114.6 512 0 397.4 0 256S114.6 0 256 0c-17.7 0-32 14.3-32 32s14.3 32 32 32z'
                      />
                      <path
                        fill='currentColor'
                        d='M224 32c0-17.7 14.3-32 32-32C397.4 0 512 114.6 512 256c0 46.6-12.5 90.4-34.3 128c-8.8 15.3-28.4 20.5-43.7 11.7s-20.5-28.4-11.7-43.7c16.3-28.2 25.7-61 25.7-96c0-106-86-192-192-192c-17.7 0-32-14.3-32-32z'
                      />
                    </svg>
                  )}
                </span>
              </button>
            ))}
          </div>
        )}
        {panelContent && (
          <div className='relative w-full flex-1 overflow-y-auto'>
            {panelContent}
            {activeTabLoading && (
              <div className='absolute inset-0 flex items-center justify-center bg-white/70'>
                <svg
                  className='h-6 w-6 animate-spin'
                  xmlns='http://www.w3.org/2000/svg'
                  viewBox='0 0 512 512'
                >
                  <path
                    fill='gray'
                    className='opacity-40'
                    d='M256 64C150 64 64 150 64 256s86 192 192 192c70.1 0 131.3-37.5 164.9-93.6l.1 .1c-6.9 14.9-1.5 32.8 13 41.2c15.3 8.9 34.9 3.6 43.7-11.7c.2-.3 .4-.6 .5-.9l0 0C434.1 460.1 351.1 512 256 512C114.6 512 0 397.4 0 256S114.6 0 256 0c-17.7 0-32 14.3-32 32s14.3 32 32 32z'
                  />
                  <path
                    fill='gray'
                    d='M224 32c0-17.7 14.3-32 32-32C397.4 0 512 114.6 512 256c0 46.6-12.5 90.4-34.3 128c-8.8 15.3-28.4 20.5-43.7 11.7s-20.5-28.4-11.7-43.7c16.3-28.2 25.7-61 25.7-96c0-106-86-192-192-192c-17.7 0-32-14.3-32-32z'
                  />
                </svg>
              </div>
            )}
          </div>
        )}
        {footerContent && (
          <div className='mt-auto border-t border-gray-200 p-3'>
            {footerContent}
          </div>
        )}
        {showDropToDelete && (
          <div className='absolute inset-x-0 bottom-0 border-t border-red-200 bg-red-100 p-2 text-center text-xs font-semibold text-red-700'>
            {dropToDeleteLabel}
          </div>
        )}
      </div>
    );
  };

  const renderMainContent = () => {
    if (isCenteredCardSection) {
      return (
        <div className='relative min-h-0 grow overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4'>
            <div className='w-full max-w-2xl rounded-lg border border-gray-200 bg-white shadow-sm'>
              {currentSectionConfig?.renderPanelContent?.()}
            </div>
          </div>
        </div>
      );
    }

    if (isFullWidthSection) {
      return (
        <>
          <div
            className={clsx(
              'relative flex min-w-0 grow flex-col overflow-hidden',
              !isPlainContent && 'rounded-lg border border-gray-200 bg-white'
            )}
          >
            <div className='w-full flex-1 overflow-y-auto'>
              {currentSectionConfig?.renderPanelContent?.()}
            </div>
          </div>
          {mainRightPanel && <div className='shrink-0'>{mainRightPanel}</div>}
        </>
      );
    }

    return (
      <>
        {!isHideSidebarPanel && (
          <div
            className={getStepSidebarPanelColumnClassName(panelVariant)}
            style={{ width: panelWidth }}
          >
            {renderPanelContent()}
            {belowPanelContent}
          </div>
        )}
        {rightContent && (
          <div className='relative flex min-w-0 grow flex-col'>
            {rightContent}
          </div>
        )}
        {mainRightPanel && <div className='shrink-0'>{mainRightPanel}</div>}
      </>
    );
  };

  const sectionTitle = currentSectionConfig?.title ?? 'Unknown Step';
  const stepNumber = getStepNumber(sections, currentSectionId);
  const stepHeaderTitle =
    centeredHeader && stepNumber !== undefined
      ? `Step ${stepNumber}: ${sectionTitle}`
      : sectionTitle;
  const headerTitle = viewOnlyMode ? viewOnlyMode.title : stepHeaderTitle;

  return (
    <div className='flex h-full w-full flex-col gap-3'>
      <div className='flex shrink-0 flex-col items-stretch gap-3 xl:flex-row'>
        <div
          className={clsx(
            'flex min-w-0 flex-1 flex-col gap-5',
            centeredHeader
              ? 'items-center'
              : 'rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm'
          )}
        >
          <div
            className={clsx(
              'flex items-center gap-4',
              hideHeaderDescription && 'pb-3'
            )}
          >
            {ringContent && <div className='shrink-0'>{ringContent}</div>}
            <div
              className={clsx(
                'flex flex-col gap-1',
                centeredHeader && 'items-center'
              )}
            >
              <div className='flex items-center gap-2'>
                <span className='text-4xl font-semibold text-gray-800'>
                  {headerTitle}
                </span>
                {!viewOnlyMode && currentStepTipSequence && (
                  <button
                    type='button'
                    className='relative flex items-center text-gray-400 hover:text-blue-500'
                    onClick={reopenTipSequence}
                    aria-label='Show step tip'
                  >
                    <QuestionMarkCircleIcon className='h-7 w-7' />
                  </button>
                )}
              </div>
              {!hideHeaderDescription && currentSectionConfig?.description && (
                <span
                  className={clsx(
                    'text-gray-500',
                    centeredHeader ? 'text-center text-lg' : 'text-base'
                  )}
                >
                  {currentSectionConfig.description}
                </span>
              )}
            </div>
          </div>
          {panelVariant === 'tabs' && !hideProgressBar && (
            <div
              className='w-full'
              style={
                progressBarMaxWidth
                  ? { maxWidth: progressBarMaxWidth }
                  : undefined
              }
            >
              <StepProgressBar
                sections={sections}
                currentSectionId={currentSectionId}
                onSectionClick={handleProgressBarClick}
              />
            </div>
          )}
        </div>
        {headerRightContent && (
          <div className='shrink-0'>{headerRightContent}</div>
        )}
      </div>
      {headerContent}
      <div className='relative flex min-h-0 grow gap-3 overflow-hidden'>
        {renderMainContent()}
      </div>
    </div>
  );
}
